import React, { useEffect, useMemo, useState } from 'react'

import { useCompanyOptionsQuery } from '../hooks/useCompanyOptionsQuery'
import { useDealsQuery } from '../hooks/useDealsQuery'
import { useFilters } from '../state/FilterProvider'
import { DealRow } from './DealRow'
import { DealSubRow } from './DealSubRow'

const PAGE_SIZE = 50

export const DealTable: React.FunctionComponent = () => {
  const { status, payment, search, isDealExpanded, setExpandedDealIds } = useFilters()
  const [page, setPage] = useState(1)

  const dealsQuery = useDealsQuery({
    page,
    pagePerRecordCount: PAGE_SIZE,
    status,
    payment,
    search: search || undefined,
  })
  const companyOptionsQuery = useCompanyOptionsQuery()
  const companyNameMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const option of companyOptionsQuery.data ?? []) {
      map.set(option.id, option.name)
    }
    return map
  }, [companyOptionsQuery.data])

  const deals = dealsQuery.data?.deals ?? []
  const total = dealsQuery.data?.total ?? 0
  const totalPages = total > 0 ? Math.ceil(total / PAGE_SIZE) : 1
  const showPager = total > PAGE_SIZE

  // Auto-expand every matched deal whenever the user has an active search,
  // so invoice rows are visible immediately. Mirrors the prototype effect.
  useEffect(() => {
    if (!search) return
    setExpandedDealIds(deals.map((deal) => deal.id))
  }, [search, deals, setExpandedDealIds])

  return (
    <div className="deal-table-wrap">
      <table className="deal-table">
        <colgroup>
          <col style={{ width: '3%' }} />
          <col style={{ width: '22%' }} />
          <col style={{ width: '30%' }} />
          <col style={{ width: '14%' }} />
          <col style={{ width: '14%' }} />
          <col style={{ width: '17%' }} />
        </colgroup>
        <thead>
          <tr>
            <th></th>
            <th>Deal</th>
            <th>Line Items</th>
            <th>Activation</th>
            <th>Billing</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {dealsQuery.isLoading ? (
            <tr>
              <td
                colSpan={6}
                style={{
                  padding: '60px 20px',
                  textAlign: 'center',
                  color: 'var(--muted)',
                  fontSize: 14,
                }}
              >
                Loading deals…
              </td>
            </tr>
          ) : null}

          {!dealsQuery.isLoading && deals.length === 0 ? (
            <tr>
              <td
                colSpan={6}
                style={{
                  padding: '60px 20px',
                  textAlign: 'center',
                  color: 'var(--muted)',
                  fontSize: 14,
                }}
              >
                No deals match the current filters.
              </td>
            </tr>
          ) : null}

          {deals.map((deal) => (
            <DealRow key={deal.id} deal={deal} companyNameMap={companyNameMap}>
              {isDealExpanded(deal.id) ? <DealSubRow deal={deal} /> : null}
            </DealRow>
          ))}
        </tbody>
      </table>

      {showPager ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 12,
            padding: '16px 20px',
            fontSize: 13,
            color: 'var(--muted)',
          }}
        >
          <button
            type="button"
            className="action-btn"
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            disabled={page <= 1}
          >
            ‹ Prev
          </button>
          <span>
            Page {page} of {totalPages} · {total} deals
          </span>
          <button
            type="button"
            className="action-btn"
            onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
            disabled={page >= totalPages}
          >
            Next ›
          </button>
        </div>
      ) : null}
    </div>
  )
}
